// @ts-nocheck - Temporary suppression for Stripe API type mismatches
import { supabase } from '@/integrations/supabase/client';

// Admin Stripe Management Functions using real Stripe API calls
// These functions allow admins to manage user subscriptions, billing, and plans

// Initialize Stripe with secret key for server-side operations
const STRIPE_SECRET_KEY = import.meta.env.VITE_STRIPE_SECRET_KEY;

if (!STRIPE_SECRET_KEY) {
  console.warn('VITE_STRIPE_SECRET_KEY is not set - Stripe operations will fail');
}

// Create Stripe instance for API calls
const createStripeInstance = async () => {
  if (!STRIPE_SECRET_KEY) {
    throw new Error('Stripe secret key not configured');
  }

  const { default: Stripe } = await import('stripe');
  return new Stripe(STRIPE_SECRET_KEY, {
    apiVersion: '2025-08-27.basil',
  });
};

export interface StripeCustomerDetails {
  id: string;
  email: string;
  name: string;
  created: number;
  default_source: string | null;
  subscriptions: {
    data: StripeSubscription[];
  };
  balance: number;
  currency: string;
  payment_methods?: {
    data: Array<{
      id: string;
      type: string;
      card?: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
      };
    }>;
  };
}

export interface StripeSubscription {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  plan: {
    id: string;
    nickname: string;
    amount: number;
    currency: string;
    interval: string;
  };
  trial_start: number | null;
  trial_end: number | null;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
}

export interface StripeInvoice {
  id: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
}

// Get comprehensive Stripe details for a user
export const getStripeCustomerDetails = async (customerId: string): Promise<StripeCustomerDetails> => {
  try {
    console.log('Getting real Stripe customer details for:', customerId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Get real customer data from Stripe
    const customer = await stripe.customers.retrieve(customerId, {
      expand: ['subscriptions', 'default_source']
    });

    if (customer.deleted) {
      throw new Error('Stripe customer has been deleted');
    }

    // Get customer's subscriptions
    const subscriptions = await stripe.subscriptions.list({
      customer: customerId,
      limit: 10,
      expand: ['data.latest_invoice', 'data.plan']
    });

    // Get customer's invoices
    const invoices = await stripe.invoices.list({
      customer: customerId,
      limit: 10
    });

    // Get customer's payment methods
    const paymentMethods = await stripe.paymentMethods.list({
      customer: customerId,
      type: 'card'
    });

    // Format the response to match our interface
    const customerDetails: StripeCustomerDetails = {
      id: customer.id,
      email: (customer as any).email || '',
      name: (customer as any).name || '',
      created: (customer as any).created,
      default_source: (customer as any).default_source,
      subscriptions: {
        data: subscriptions.data.map((sub: any) => ({
          id: sub.id,
          status: sub.status,
          current_period_start: sub.current_period_start,
          current_period_end: sub.current_period_end,
          plan: {
            id: sub.items.data[0]?.price.id || '',
            nickname: sub.items.data[0]?.price.nickname || '',
            amount: sub.items.data[0]?.price.unit_amount || 0,
            currency: sub.items.data[0]?.price.currency || 'usd',
            interval: sub.items.data[0]?.price.recurring?.interval || 'month'
          },
          trial_start: sub.trial_start,
          trial_end: sub.trial_end,
          cancel_at_period_end: sub.cancel_at_period_end,
          canceled_at: sub.canceled_at
        }))
      },
      balance: (customer as any).balance || 0,
      currency: (customer as any).currency || 'usd',
      payment_methods: { data: (paymentMethods as any).data || [] }
    };

    console.log('Real Stripe customer details retrieved:', customerDetails);
    return customerDetails;
  } catch (error) {
    console.error('Error fetching Stripe customer details:', error);
    throw error;
  }
};

// Get customer invoices
export const getCustomerInvoices = async (customerId: string): Promise<StripeInvoice[]> => {
  try {
    console.log('Getting real customer invoices for:', customerId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Get real invoices from Stripe
    const invoices = await stripe.invoices.list({
      customer: customerId,
      limit: 20,
      expand: ['data.subscription', 'data.payment_intent']
    });

    return invoices.data.map(invoice => ({
      id: invoice.id,
      amount_paid: invoice.amount_paid,
      amount_due: invoice.amount_due,
      currency: invoice.currency,
      status: invoice.status,
      created: invoice.created,
      period_start: invoice.period_start || invoice.created,
      period_end: invoice.period_end || invoice.created,
      hosted_invoice_url: invoice.hosted_invoice_url,
      invoice_pdf: invoice.invoice_pdf,
      subscription: invoice.subscription
    }));
  } catch (error) {
    console.error('Error fetching customer invoices:', error);
    throw error;
  }
};

// Admin: Change user's subscription plan
export const adminChangeUserPlan = async (customerId: string, newPriceId: string) => {
  try {
    console.log('Admin changing user plan:', customerId, 'to price:', newPriceId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Get customer's current subscriptions
    const subscriptions = await stripe.subscriptions.list({
      customer: customerId,
      status: 'active',
      limit: 1
    });

    if (subscriptions.data.length === 0) {
      // Create new subscription if none exists
      const subscription = await stripe.subscriptions.create({
        customer: customerId,
        items: [{ price: newPriceId }],
        metadata: {
          changed_by: 'admin',
          changed_at: new Date().toISOString()
        }
      });

      // Update user profile
      await supabase
        .from('profiles')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('stripe_customer_id', customerId);

      return {
        subscription_id: subscription.id,
        customer_id: customerId,
        new_price_id: newPriceId,
        status: subscription.status,
        message: 'New subscription created successfully'
      };
    } else {
      // Update existing subscription
      const currentSub = subscriptions.data[0];
      const updatedSub = await stripe.subscriptions.update(currentSub.id, {
        items: [{
          id: currentSub.items.data[0].id,
          price: newPriceId
        }],
        proration_behavior: 'create_prorations',
        metadata: {
          changed_by: 'admin',
          changed_at: new Date().toISOString()
        }
      });

      // Update user profile
      await supabase
        .from('profiles')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('stripe_customer_id', customerId);

      return {
        subscription_id: updatedSub.id,
        customer_id: customerId,
        new_price_id: newPriceId,
        status: updatedSub.status,
        message: 'Subscription plan changed successfully'
      };
    }
  } catch (error) {
    console.error('Error changing user plan:', error);
    throw error;
  }
};

// Admin: Pause user subscription
export const adminPauseSubscription = async (subscriptionId: string) => {
  try {
    console.log('Admin pausing subscription:', subscriptionId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Pause the subscription in Stripe
    const subscription = await stripe.subscriptions.update(subscriptionId, {
      pause_collection: {
        behavior: 'mark_uncollectible'
      },
      metadata: {
        paused_by: 'admin',
        paused_at: new Date().toISOString()
      }
    });

    // Update user profile in Supabase
    await supabase
      .from('profiles')
      .update({
        updated_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', subscriptionId);

    return {
      subscription_id: subscription.id,
      status: subscription.status,
      pause_collection: subscription.pause_collection,
      message: 'Subscription paused successfully'
    };
  } catch (error) {
    console.error('Error pausing subscription:', error);
    throw error;
  }
};

// Admin: Resume user subscription
export const adminResumeSubscription = async (subscriptionId: string) => {
  try {
    console.log('Admin resuming subscription:', subscriptionId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Resume the subscription in Stripe
    const subscription = await stripe.subscriptions.update(subscriptionId, {
      pause_collection: null, // Remove pause
      metadata: {
        resumed_by: 'admin',
        resumed_at: new Date().toISOString()
      }
    });

    // Update user profile in Supabase
    await supabase
      .from('profiles')
      .update({
        updated_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', subscriptionId);

    return {
      subscription_id: subscription.id,
      status: subscription.status,
      message: 'Subscription resumed successfully'
    };
  } catch (error) {
    console.error('Error resuming subscription:', error);
    throw error;
  }
};

// Admin: Cancel user subscription
export const adminCancelSubscription = async (subscriptionId: string, cancelAtPeriodEnd: boolean = true) => {
  try {
    console.log('Admin canceling subscription:', subscriptionId, 'at period end:', cancelAtPeriodEnd);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Cancel the subscription in Stripe
    let subscription;
    if (cancelAtPeriodEnd) {
      subscription = await stripe.subscriptions.update(subscriptionId, {
        cancel_at_period_end: true,
        metadata: {
          canceled_by: 'admin',
          canceled_at: new Date().toISOString()
        }
      });
    } else {
      subscription = await stripe.subscriptions.cancel(subscriptionId, {
        metadata: {
          canceled_by: 'admin',
          canceled_at: new Date().toISOString()
        }
      });
    }

    // Update user profile in Supabase
    await supabase
      .from('profiles')
      .update({
        updated_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', subscriptionId);

    return {
      subscription_id: subscription.id,
      status: subscription.status,
      cancel_at_period_end: subscription.cancel_at_period_end,
      canceled_at: subscription.canceled_at,
      message: 'Subscription canceled successfully'
    };
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

// Admin: Update subscription billing cycle dates
export const adminUpdateBillingCycle = async (subscriptionId: string, newPeriodEnd: number) => {
  try {
    console.log('Admin updating billing cycle for subscription:', subscriptionId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Update subscription billing cycle
    const subscription = await stripe.subscriptions.update(subscriptionId, {
      billing_cycle_anchor: newPeriodEnd,
      proration_behavior: 'none',
      metadata: {
        billing_updated_by: 'admin',
        billing_updated_at: new Date().toISOString()
      }
    });

    // Update user profile in Supabase
    await supabase
      .from('profiles')
      .update({
        updated_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', subscriptionId);

    return {
      subscription_id: subscription.id,
      current_period_end: subscription.current_period_end,
      billing_cycle_anchor: subscription.billing_cycle_anchor,
      message: 'Billing cycle updated successfully'
    };
  } catch (error) {
    console.error('Error updating billing cycle:', error);
    throw error;
  }
};

// Admin: Apply discount/coupon to customer
export const adminApplyDiscount = async (customerId: string, couponId: string) => {
  try {
    console.log('Admin applying coupon:', couponId, 'to customer:', customerId);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Update customer with coupon
    const customer = await stripe.customers.update(customerId, {
      coupon: couponId,
      metadata: {
        coupon_applied_by: 'admin',
        coupon_applied_at: new Date().toISOString()
      }
    });

    return {
      customer_id: customer.id,
      coupon: customer.discount?.coupon,
      discount: customer.discount,
      message: 'Coupon applied successfully'
    };
  } catch (error) {
    console.error('Error applying discount:', error);
    throw error;
  }
};

// Admin: Create manual invoice for customer
export const adminCreateInvoice = async (customerId: string, amount: number, description: string) => {
  try {
    console.log('Admin creating invoice for:', customerId, 'amount:', amount);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Create invoice item
    const invoiceItem = await stripe.invoiceItems.create({
      customer: customerId,
      amount: amount * 100, // Convert to cents
      currency: 'usd',
      description: description,
      metadata: {
        created_by: 'admin',
        created_at: new Date().toISOString()
      }
    });

    // Create and finalize invoice
    const invoice = await stripe.invoices.create({
      customer: customerId,
      auto_advance: true,
      metadata: {
        created_by: 'admin',
        created_at: new Date().toISOString()
      }
    });

    const finalizedInvoice = await stripe.invoices.finalizeInvoice(invoice.id);

    return {
      invoice_id: finalizedInvoice.id,
      customer_id: customerId,
      amount: amount,
      currency: 'usd',
      description: description,
      status: finalizedInvoice.status,
      hosted_invoice_url: finalizedInvoice.hosted_invoice_url,
      invoice_pdf: finalizedInvoice.invoice_pdf,
      created_at: new Date(finalizedInvoice.created * 1000).toISOString(),
      message: 'Invoice created successfully'
    };
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Admin: Refund payment
export const adminRefundPayment = async (paymentIntentId: string, amount?: number) => {
  try {
    console.log('Admin processing refund for payment:', paymentIntentId, 'amount:', amount);

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Create refund
    const refund = await stripe.refunds.create({
      payment_intent: paymentIntentId,
      amount: amount ? amount * 100 : undefined, // Convert to cents if specified
      metadata: {
        processed_by: 'admin',
        processed_at: new Date().toISOString()
      }
    });

    return {
      refund_id: refund.id,
      payment_intent: refund.payment_intent,
      amount: refund.amount / 100, // Convert back to dollars
      currency: refund.currency,
      status: refund.status,
      created_at: new Date(refund.created * 1000).toISOString(),
      message: 'Refund processed successfully'
    };
  } catch (error) {
    console.error('Error processing refund:', error);
    throw error;
  }
};

// Get all available Stripe plans/prices
export const getAvailableStripePlans = async () => {
  try {
    console.log('Getting available Stripe plans');

    // Initialize Stripe
    const stripe = await createStripeInstance();

    // Get all active prices from Stripe
    const prices = await stripe.prices.list({
      active: true,
      expand: ['data.product'],
      limit: 100
    });

    // Format prices for admin interface
    const plans = prices.data.map(price => ({
      id: price.id,
      product_id: price.product,
      nickname: price.nickname || (price.product as any)?.name || 'Unnamed Plan',
      amount: price.unit_amount || 0,
      currency: price.currency,
      interval: price.recurring?.interval || 'one_time',
      interval_count: price.recurring?.interval_count || 1,
      type: price.type,
      active: price.active,
      created: price.created,
      metadata: price.metadata
    }));

    console.log('Retrieved Stripe plans:', plans.length);
    return plans;
  } catch (error) {
    console.error('Error fetching Stripe plans:', error);
    throw error;
  }
};

// Admin: Get comprehensive user billing summary
export const getUserBillingSummary = async (userId: string) => {
  try {
    // Get user profile with Stripe customer ID
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('stripe_customer_id, email, name, subscription_status')
      .eq('id', userId)
      .single();

    if (profileError || !profile?.stripe_customer_id) {
      throw new Error('User has no Stripe customer ID');
    }

    // Get Stripe customer details
    const customerDetails = await getStripeCustomerDetails(profile.stripe_customer_id);
    
    // Get recent invoices
    const invoices = await getCustomerInvoices(profile.stripe_customer_id);

    return {
      profile,
      customer: customerDetails,
      invoices,
      hasActiveSubscription: customerDetails.subscriptions.data.length > 0,
      activeSubscription: customerDetails.subscriptions.data.find(sub => 
        ['active', 'trialing', 'past_due'].includes(sub.status)
      )
    };
  } catch (error) {
    console.error('Error getting user billing summary:', error);
    throw error;
  }
};
